// Shared email validation + typo suggestions used by parts forms and parts admin.

const EMAIL_RE = /^[^\s@,;:"'()[\]\\<>]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,24}$/;

/** True when the address is structurally valid (requires a real dotted domain + TLD). */
export function isValidEmail(value: string | null | undefined): boolean {
  const email = String(value ?? "").trim();
  if (!email || email.length > 254) return false;
  if (email.includes("..")) return false;
  const at = email.lastIndexOf("@");
  if (at < 1) return false;
  const local = email.slice(0, at);
  if (local.length > 64 || local.startsWith(".") || local.endsWith(".")) return false;
  return EMAIL_RE.test(email);
}

// Domains people mistype most often, plus the missing-dot variants (gmailcom).
const DOMAIN_FIXES: Record<string, string> = {
  gmailcom: "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.cm": "gmail.com",
  "gmail.con": "gmail.com",
  "gmial.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gnail.com": "gmail.com",
  "gmail.comm": "gmail.com",
  yahoocom: "yahoo.com",
  "yahoo.co": "yahoo.com",
  "yahoo.con": "yahoo.com",
  "yaho.com": "yahoo.com",
  hotmailcom: "hotmail.com",
  "hotmail.co": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "hotmial.com": "hotmail.com",
  outlookcom: "outlook.com",
  "outlook.co": "outlook.com",
  "outlook.con": "outlook.com",
  aolcom: "aol.com",
  "aol.con": "aol.com",
  icloudcom: "icloud.com",
  "icloud.co": "icloud.com",
  comcastnet: "comcast.net",
  "comcast.ne": "comcast.net",
  bellsouthnet: "bellsouth.net",
  charternet: "charter.net",
  "att.ne": "att.net",
  attnet: "att.net",
};

/**
 * Returns a corrected address when the entry looks like a common typo,
 * otherwise null. Never auto-applies — always offer it to the user.
 */
export function suggestEmailFix(value: string | null | undefined): string | null {
  const email = String(value ?? "").trim();
  const at = email.lastIndexOf("@");
  if (at < 1) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1).toLowerCase().replace(/\.+$/, "");
  if (!domain) return null;

  const mapped = DOMAIN_FIXES[domain];
  if (mapped) return `${local}@${mapped}`;

  // Missing dot before a known TLD, e.g. "somedomaincom" -> "somedomain.com"
  if (!domain.includes(".")) {
    for (const tld of ["com", "net", "org", "edu", "gov", "us", "co"]) {
      if (domain.length > tld.length + 1 && domain.endsWith(tld)) {
        return `${local}@${domain.slice(0, -tld.length)}.${tld}`;
      }
    }
  }
  return null;
}

/** Human-readable problem message, or null when the address is fine. */
export function emailError(value: string | null | undefined, required = true): string | null {
  const email = String(value ?? "").trim();
  if (!email) return required ? "Email address is required." : null;
  if (isValidEmail(email)) return null;
  const fix = suggestEmailFix(email);
  return fix
    ? `That email address doesn't look right. Did you mean ${fix}?`
    : "Please enter a valid email address (like name@example.com).";
}
